import { useCallback } from "react";
import { useHistory } from "react-router-dom";
import { useSnackbar } from "react-simple-snackbar";

import useAuth from "$hooks/useAuth";
import { EDBService } from "$services";
import { DispositionType } from "$types/EDB";
import { ProfileEDBModalsTypes } from "../types/profileEDB.types";
import { useProfileEDBContext } from "$containers/EDB/ProfileEDB/context/ProfileEDBContext";
import { snackbarSuccessOptions } from "$utils/SnackbarOptions";

export default function useModals(): ProfileEDBModalsTypes {
  const history = useHistory();
  const { token } = useAuth();
  const [openSnackbar] = useSnackbar(snackbarSuccessOptions);

  const {
    edb,
    idEdb,
    fetchEdb,
    integratedOrders,
    productsQuantityMap,
    productToDeleteList,

    onDeleteEdb,
    onCancelEdb,

    setVal,
    resetDelete,
    setIsDeleteEdb,
    setIsCancelEdb,
    setIsDuplicateEdb,
    getProductInOP,
    setIsModifyingEdb,
    setProductsQuantityMap,
    setProductToDeleteList,
    setOpenModalDeleteMultipleProduct,
  } = useProfileEDBContext();

  const deleteProduct = useCallback(
    async (productId: string) => {
      await EDBService.DeleteEDBItemProduct(token, edb._id, productId);
      fetchEdb();
    },
    [edb, fetchEdb, token]
  );

  const deleteMultipleProduct = useCallback(async () => {
    productToDeleteList.forEach(async (productId) => {
      deleteProduct(productId);
    });
    resetDelete();
  }, [productToDeleteList, resetDelete, deleteProduct]);

  const handleDeleteEdb = useCallback(async () => {
    setIsDeleteEdb(false);
    await onDeleteEdb(idEdb);
    history.push("/edbs");
  }, [history, idEdb, onDeleteEdb, setIsDeleteEdb]);

  const handleCancelDeleteEdb = useCallback(() => {
    setIsDeleteEdb(false);
  }, [setIsDeleteEdb]);

  const handleCancelEdb = useCallback(async () => {
    setIsCancelEdb(false);
    await onCancelEdb(idEdb);
  }, [idEdb, onCancelEdb, setIsCancelEdb]);

  const handleCancelCancelEdb = useCallback(() => {
    setIsCancelEdb(false);
  }, [setIsCancelEdb]);

  const handleDuplicateEdb = useCallback(async () => {
    setIsDuplicateEdb(false);
    const duplicated = await EDBService.DuplicateEDB(token, idEdb);
    const newId = duplicated?._id ?? duplicated?.id;
    const newEdbNumber = duplicated?.edbId;
    if (newId) {
      history.push(`/create-edb/${newId}`);
    }
    if (newEdbNumber) {
      openSnackbar(`EDB n° ${newEdbNumber} créée avec succès`);
    }
  }, [history, idEdb, openSnackbar, setIsDuplicateEdb, token]);

  const handleCancelDuplicateEdb = useCallback(() => {
    setIsDuplicateEdb(false);
  }, [setIsDuplicateEdb]);

  const handleChangeProductQty = useCallback(
    (qty: any, edbProduct: any) => {
      const qtyProductsInOP = getProductInOP(edbProduct.relatedProduct._id);

      if (qty === "") {
        setProductsQuantityMap(productsQuantityMap.set(edbProduct._id, qty));
        setVal(1);
      } else if (qty < qtyProductsInOP) {
        setProductsQuantityMap(
          productsQuantityMap.set(edbProduct._id, qtyProductsInOP)
        );
        setVal(parseInt(qty, 10));
      } else {
        setProductsQuantityMap(
          productsQuantityMap.set(edbProduct._id, parseInt(qty, 10))
        );
        setVal(parseInt(qty, 10));
      }
    },
    [getProductInOP, productsQuantityMap, setProductsQuantityMap, setVal]
  );

  const updateProducts = useCallback(async () => {
    const newProducts = [];
    const tmpToDelete = [];
    productsQuantityMap.forEach((value, key) => {
      const foundProduct = edb?.products.find((pr: any) => pr._id === key);
      if (foundProduct) {
        if (value === 0) {
          tmpToDelete.push(foundProduct.relatedProduct._id);
        } else {
          newProducts.push({
            // Backend expects Product id (connect on `products.id`)
            _id: foundProduct.relatedProduct._id,
            qty: value,
          });
        }
      }
    });
    setProductToDeleteList(tmpToDelete);
    if (tmpToDelete.length > 0) {
      setOpenModalDeleteMultipleProduct(true);
    } else {
      setIsModifyingEdb(false);
      await EDBService.UpdateEDBProducts(
        token,
        { newProducts, integratedOrders },
        edb._id
      );

      productsQuantityMap.clear();
      setProductsQuantityMap(productsQuantityMap);
      fetchEdb();
    }
  }, [
    edb,
    fetchEdb,
    integratedOrders,
    productsQuantityMap,
    token,
    setIsModifyingEdb,
    setOpenModalDeleteMultipleProduct,
    setProductToDeleteList,
    setProductsQuantityMap,
  ]);

  const getWordingProductToDelete = useCallback(() => {
    if (productToDeleteList.length > 1) {
      return `${productToDeleteList.length} produits ont une quantité de 0. Ils vont donc être supprimés de l'expression de besoin.`;
    }
    return `${productToDeleteList.length} produit a une quantité de 0. Il va donc être supprimé de l'expression de besoin`;
  }, [productToDeleteList.length]);

  const getDeliveryModality = useCallback(() => {
    if (edb) {
      if (edb.dispositionType) {
        return `${edb.preparationType} - ${edb.dispositionType}`;
      }
      return edb.preparationType;
    }
    return "";
  }, [edb]);

  const getInformationDeliveryAddress = useCallback(() => {
    if (edb) {
      if (edb.dispositionType === DispositionType.ON_DOCKYARD) {
        return `${edb.dockyard?.name}
           ${edb.dockyard?.address} ${edb.dockyard?.address2} ${edb.dockyard?.postalCode} ${edb.dockyard?.city}, ${edb.dockyard?.country}`;
      }
      if (edb.dispositionType === DispositionType.SUBCONTRACTED_WAREHOUSE) {
        return `${edb.entrepotName}
           ${edb.dispositionAdress} ${edb.dispositionAdress2} ${edb.dispositionPostalCode} ${edb.dispositionCity}, ${edb.dispositionCountry}`;
      }
      if (edb.dispositionType === DispositionType.OTHER_PLACE) {
        return `${edb.dispositionPlace}
          ${edb.dispositionAdress} ${edb.dispositionAdress2} ${edb.dispositionPostalCode} ${edb.dispositionCity}, ${edb.dispositionCountry}`;
      }
    }
    return "";
  }, [edb]);

  return {
    deleteProduct,
    updateProducts,
    handleDeleteEdb,
    handleCancelEdb,
    handleDuplicateEdb,
    getDeliveryModality,
    deleteMultipleProduct,
    handleCancelDeleteEdb,
    handleCancelCancelEdb,
    handleCancelDuplicateEdb,
    handleChangeProductQty,
    getWordingProductToDelete,
    getInformationDeliveryAddress,
  };
}
